/**
 * The tabs above the hero — Demo, then one per YouTube material. Variant D of the 25 September 2026
 * canvas, chosen by the owner the same day.
 *
 * Drawn only when there is a film to switch to: a movement with no YouTube material keeps the
 * canon's card exactly, hero straight under the bar. The strip is the app's one `TabStrip`, so the
 * roving stop, the arrows and the ember rule are the client detail's and `/raporty`'s.
 *
 * Each tab carries its picture — the hero's still for the demo, YouTube's own still for a film — so
 * the choice is legible before it is made.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TabStrip } from '~/shared/ui/TabStrip';

import { LinkBrandMark, youtubeThumbnailUrl } from '~/entities/exercise-library';

import { DEMO_SOURCE, type FilmSource } from '../model/theatreSources';

import styles from './TheatreSources.module.css';

interface TheatreSourcesProps {
  readonly sources: readonly FilmSource[];
  readonly selected: string;
  readonly onSelect: (key: string) => void;
  /** The demo tab's picture: the hero's own still, or `null` when the movement has none. */
  readonly demoStill: string | null;
}

export function TheatreSources({ sources, selected, onSelect, demoStill }: TheatreSourcesProps) {
  const { t } = useTranslation();
  const byKey = new Map(sources.map((source) => [source.key, source]));

  return (
    <TabStrip
      label={t('web:exerciseLibrary.theatre.youtube.sources')}
      tabs={[DEMO_SOURCE, ...sources.map((source) => source.key)]}
      selected={selected}
      onSelect={onSelect}
      className={styles.bar}
      renderLabel={(key) => {
        const source = byKey.get(key);
        if (source === undefined) {
          return (
            <span className={styles.label}>
              <TabThumb src={demoStill} />
              {t('web:exerciseLibrary.theatre.youtube.demo')}
            </span>
          );
        }
        return (
          <span className={styles.label}>
            <TabThumb src={youtubeThumbnailUrl(source.video)} />
            <span className={styles.mark}>
              <LinkBrandMark brand="youtube" className={styles.markGlyph} />
            </span>
            <span className={styles.word}>{source.label}</span>
          </span>
        );
      }}
    />
  );
}

/**
 * A tab's picture, or the blank plate in its place. A still that does not load — a demo whose
 * poster the media root lost, YouTube's image host blocked by a network — draws the browser's
 * broken-file mark otherwise, inside a tab label.
 */
function TabThumb({ src }: { readonly src: string | null }) {
  const [failed, setFailed] = useState(false);
  if (src === null || failed) return <span className={styles.thumb} />;
  return (
    <img
      className={styles.thumb}
      src={src}
      alt=""
      decoding="async"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}
